import React, { useState, useEffect, useContext } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, Calendar, User, Building, Activity, Shield, 
  AlertTriangle, Clock, Mail, UserCheck, Edit, CheckCircle2,
  Clock3, PlayCircle, ListTodo, Check, X, Paperclip, Download, Trash2, Upload
} from 'lucide-react';
import AuthContext from '../../../../context/AuthContext';
import { useToast } from '../../../../context/ToastContext';
import useLoader from '../../../../context/useLoader';
import taskService from '../../../../services/taskService';

export default function TaskDetails() {
  const { taskId } = useParams();
  const navigate = useNavigate();
  const { authTokens } = useContext(AuthContext);
  const { showSuccess, showError } = useToast();
  const { isLoading, showLoader, hideLoader } = useLoader();
  
  const [task, setTask] = useState(null);
  const [attachments, setAttachments] = useState([]);
  const [loadingAttachments, setLoadingAttachments] = useState(false);
  
  // Delete confirmation modal state
  const [deleteModal, setDeleteModal] = useState({
    isOpen: false,
    attachmentId: null,
    fileName: ''
  });
  
  // Upload modal state
  const [uploadModal, setUploadModal] = useState({
    isOpen: false
  });
  
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [isUploading, setIsUploading] = useState(false);

  useEffect(() => {
    fetchTaskDetails();
  }, [taskId]);

  useEffect(() => {
    if (task) {
      fetchTaskAttachments();
    }
  }, [task]);

  const fetchTaskDetails = async () => {
    showLoader('Chargement des détails de la tâche...');
      
    try {
      const result = await taskService.getTaskDetails(taskId, authTokens.access);
      
      if (result.success) {
        setTask(result.data);
      } else {
        showError(`Erreur lors du chargement: ${result.error}`);
        setTimeout(() => navigate(-1), 2000);
      }
    } catch (error) {
      console.error('Error fetching task details:', error);
      showError('Impossible de charger les détails de la tâche');
      setTimeout(() => navigate(-1), 2000);
    } finally {
      hideLoader();
    }
  };

  const fetchTaskAttachments = async () => {
    setLoadingAttachments(true);
    try {
      const result = await taskService.getTaskAttachments(taskId, authTokens.access);
      if (result.success) {
        setAttachments(result.data);
      } else {
        console.error('Error fetching attachments:', result.error);
      }
    } catch (error) {
      console.error('Error fetching attachments:', error);
    } finally {
      setLoadingAttachments(false);
    }
  };

  const handleDownloadAttachment = async (attachmentId, fileName) => {
    try {
      const result = await taskService.downloadTaskAttachment(attachmentId, authTokens.access);
      
      if (result.success) {
        // Create blob and download
        const blob = await result.response.blob();
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = fileName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        
        showSuccess(`Téléchargement de ${fileName} commencé`);
      } else {
        showError(`Erreur lors du téléchargement: ${result.error}`);
      }
    } catch (error) {
      showError('Erreur lors du téléchargement du fichier');
    }
  };

  const handleDeleteAttachment = async (attachmentId, fileName) => {
    try {
      const result = await taskService.deleteTaskAttachment(attachmentId, authTokens.access);
      
      if (result.success) {
        showSuccess(`${fileName} supprimé avec succès`);
        fetchTaskAttachments(); // Refresh attachments list
        setDeleteModal({ isOpen: false, attachmentId: null, fileName: '' });
      } else {
        showError(`Erreur lors de la suppression: ${result.error}`);
      }
    } catch (error) {
      showError('Erreur lors de la suppression du fichier');
    }
  };

  const openDeleteModal = (attachmentId, fileName) => {
    setDeleteModal({ isOpen: true, attachmentId, fileName });
  };

  const closeDeleteModal = () => {
    setDeleteModal({ isOpen: false, attachmentId: null, fileName: '' });
  };

  const confirmDelete = () => {
    if (deleteModal.attachmentId && deleteModal.fileName) {
      handleDeleteAttachment(deleteModal.attachmentId, deleteModal.fileName);
    }
  };

  const openUploadModal = () => {
    setUploadModal({ isOpen: true });
    setSelectedFiles([]);
  };

  const closeUploadModal = () => {
    setUploadModal({ isOpen: false });
    setSelectedFiles([]);
  };

  const handleFileSelection = (event) => {
    const files = Array.from(event.target.files);
    const validFiles = [];
    
    for (const file of files) {
      if (!taskService.isValidFileType(file.name)) {
        showError(`Type de fichier non autorisé: ${file.name}`);
        continue;
      }
      
      if (!taskService.isValidFileSize(file.size)) {
        showError(`Fichier trop volumineux: ${file.name} (max 5MB)`);
        continue;
      }
      
      validFiles.push(file);
    }
    
    setSelectedFiles(prev => [...prev, ...validFiles]);
  };

  const removeSelectedFile = (index) => {
    setSelectedFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleUploadFiles = async () => {
    if (selectedFiles.length === 0) {
      showError('Veuillez sélectionner au moins un fichier');
      return;
    }

    setIsUploading(true);
    let successCount = 0;
    let errorCount = 0;

    for (const file of selectedFiles) {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('description', '');

      try {
        const result = await taskService.uploadTaskAttachment(taskId, formData, authTokens.access);

        if (result.success) {
          successCount++;
        } else {
          errorCount++;
          showError(`Erreur lors de l'upload de ${file.name}: ${result.error}`);
        }
      } catch (error) {
        errorCount++;
        showError(`Erreur lors de l'upload de ${file.name}`);
      }
    }

    if (successCount > 0) {
      showSuccess(`${successCount} fichier(s) uploadé(s) avec succès`);
      fetchTaskAttachments(); // Refresh attachments list
    }
    
    setIsUploading(false);
    closeUploadModal();
  };

  const getStatusConfig = (status) => {
    const statusMap = {
      'pending': { 
        color: 'bg-yellow-100 text-yellow-800 border-yellow-300', 
        label: 'En attente',
        icon: ListTodo,
        iconColor: 'text-yellow-600'
      },
      'accepted': { 
        color: 'bg-blue-100 text-blue-800 border-blue-300', 
        label: 'Acceptée',
        icon: Check,
        iconColor: 'text-blue-600'
      },
      'rejected': { 
        color: 'bg-red-100 text-red-800 border-red-300', 
        label: 'Rejetée',
        icon: X,
        iconColor: 'text-red-600'
      },
      'in_progress': { 
        color: 'bg-orange-100 text-orange-800 border-orange-300', 
        label: 'En cours',
        icon: PlayCircle,
        iconColor: 'text-orange-600'
      },
      'completed': { 
        color: 'bg-green-100 text-green-800 border-green-300', 
        label: 'Terminée',
        icon: CheckCircle2,
        iconColor: 'text-green-600'
      }
    };
    
    return statusMap[status] || statusMap['pending'];
  };

  const getStatusBadge = (status) => {
    const config = getStatusConfig(status);
    const StatusIcon = config.icon;
    
    return (
      <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium border ${config.color}`}>
        <StatusIcon className={`h-3.5 w-3.5 ${config.iconColor}`} />
        {config.label}
      </span>
    );
  };

  const getProbabilityBadge = (probability) => {
    const probConfig = {
      low: { color: 'bg-green-100 text-green-800', label: 'Faible' },
      medium: { color: 'bg-yellow-100 text-yellow-800', label: 'Moyenne' },
      high: { color: 'bg-red-100 text-red-800', label: 'Élevée' }
    };
    
    const config = probConfig[probability?.toLowerCase()] || { 
      color: 'bg-gray-100 text-gray-800', 
      label: probability || 'Non définie' 
    };
    
    return (
      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${config.color}`}>
        {config.label}
      </span>
    );
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Non définie';
    return new Date(dateString).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: 'long',
      year: 'numeric'
    });
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return 'Non définie';
    return new Date(dateString).toLocaleString('fr-FR', {
      day: '2-digit',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const isOverdue = (dueDate) => {
    if (!dueDate) return false;
    return new Date(dueDate) < new Date();
  };

  if (!task) {
    return <div className="flex justify-center items-center h-64 text-sm">Chargement...</div>;
  }

  return (
    <div className="max-w-6xl mx-auto space-y-5">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <button 
            onClick={() => navigate(-1)}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            disabled={isLoading}
          >
            <ArrowLeft className="h-5 w-5 text-gray-500" />
          </button>
          <div>
            <h1 className="text-xl font-bold text-gray-900">{task.name}</h1>
            <p className="text-xs text-gray-500">Détails de la tâche</p>
          </div>
        </div>
        
        <div className="flex items-center space-x-2">
          {getStatusBadge(task.status)}
        </div>
      </div>

      {/* Task Information */}

      <div className="bg-white border border-gray-200 rounded-lg p-5 shadow-sm">
        <div className="flex items-center mb-4">
          <ListTodo className="h-4 w-4 text-teal-600 mr-2" />
          <h2 className="text-base font-semibold text-gray-900">Informations de la Tâche</h2>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Nom de la tâche</label>
            <p className="text-sm text-gray-900">{task.name}</p>
          </div>
          
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Statut</label>
            <div>{getStatusBadge(task.status)}</div>
          </div>
          {task.accepted_date != null && (
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Date d'acceptation/refus</label>
            <div className="flex items-center text-sm text-gray-900">
              <Calendar className="h-3.5 w-3.5 mr-1.5 text-gray-400" />
              {formatDateTime(task.accepted_date)}
            </div>
          </div>
          )}
          {task.task_completion_date != null && (
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Date Completion</label>
              <div className="flex items-center text-sm text-gray-900">
                <Calendar className="h-3.5 w-3.5 mr-1.5 text-gray-400" />
                {formatDateTime(task.task_completion_date)}
              </div>
            </div>
          )}
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Date de création</label>
            <div className="flex items-center text-sm text-gray-900">
              <Calendar className="h-3.5 w-3.5 mr-1.5 text-gray-400" />
              {formatDateTime(task.created_at)}
            </div>
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Dernière mise à jour</label>
            <div className="flex items-center text-sm text-gray-900">
              <Clock className="h-3.5 w-3.5 mr-1.5 text-gray-400" />
              {formatDateTime(task.updated_at)}
            </div>
          </div>
        </div>

        {task.comment &&  (
          <div className='mt-2'>
            <p className="text-base font-semibold text-gray-700 ">Commentaire</p>
            <p className="text-sm text-gray-900">{task.comment}</p>
          </div>
        )}
      </div>

      {/* Assigned User */}
      {task.assigned_to_details && (
        <div className="bg-white border border-gray-200 rounded-lg p-5 shadow-sm">
          <div className="flex items-center mb-4">
            <UserCheck className="h-4 w-4 text-blue-600 mr-2" />
            <h2 className="text-base font-semibold text-gray-900">Assigné à</h2>
          </div>
          
          <div className="flex items-center space-x-4">
            <div className="h-12 w-12 rounded-full bg-blue-100 flex items-center justify-center">
              <User className="h-6 w-6 text-blue-600" />
            </div>
            <div>
              <p className="text-sm font-medium text-gray-900">
                {task.assigned_to_details.first_name} {task.assigned_to_details.last_name}
              </p>
              <div className="flex items-center text-xs text-gray-500 mt-0.5">
                <Mail className="h-3 w-3 mr-1" />
                {task.assigned_to_details.email}
              </div>
              
              
            </div>
          </div>
        </div>
      )}

      {/* Process Details */}
      {task.process_details && (
        <div className="bg-white border border-gray-200 rounded-lg p-5 shadow-sm">
          <div className="flex items-center mb-4">
            <Building className="h-4 w-4 text-purple-600 mr-2" />
            <h2 className="text-base font-semibold text-gray-900">Processus Associé</h2>
          </div>
          
          <div className="space-y-3">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Nom du processus</label>
              <p className="text-sm text-gray-900">{task.process_details.process_name}</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Point focal</label>
                <div className="flex items-center text-sm text-gray-900">
                  <User className="h-3.5 w-3.5 mr-1.5 text-gray-400" />
                  {task.process_details.focal_point || 'Non défini'}
                </div>
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Élément à contrôler</label>
                <p className="text-sm text-gray-900">{task.process_details.element_to_controle || 'Non défini'}</p>
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Date du processus</label>
                <div className="flex items-center text-sm text-gray-900">
                  <Calendar className="h-3.5 w-3.5 mr-1.5 text-gray-400" />
                  {formatDate(task.process_details.process_date)}
                </div>
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Date de contrôle</label>
                <div className="flex items-center text-sm text-gray-900">
                  <Calendar className="h-3.5 w-3.5 mr-1.5 text-gray-400" />
                  {formatDate(task.process_details.controleDate)}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Risk Action Details */}
      {task.risk_action_details && (
        <div className="bg-white border border-gray-200 rounded-lg p-5 shadow-sm">
          <div className="flex items-center mb-4">
            <AlertTriangle className="h-4 w-4 text-orange-600 mr-2" />
            <h2 className="text-base font-semibold text-gray-900">Action de Risque Associée</h2>
          </div>
          
          <div className="space-y-4">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Description de l'action</label>
              <p className="text-sm text-gray-900">{task.risk_action_details.action}</p>
            </div>

            {task.risk_action_details.dueDate && (
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Date d'échéance</label>
                <div className={`flex items-center text-sm ${
                  isOverdue(task.risk_action_details.dueDate) ? 'text-red-600' : 'text-gray-900'
                }`}>
                  <Clock className="h-3.5 w-3.5 mr-1.5" />
                  {formatDate(task.risk_action_details.dueDate)}
                  {isOverdue(task.risk_action_details.dueDate) && (
                    <span className="ml-2 px-2 py-0.5 bg-red-100 text-red-800 rounded text-xs font-medium">
                      En retard
                    </span>
                  )}
                </div>
              </div>
            )}

            {/* Assigned User from Risk Action */}
            {task.risk_action_details.assignedUserObject && (
              <div className="border-t pt-4">
                <label className="block text-xs font-medium text-gray-700 mb-2">Utilisateur assigné (depuis l'action de risque)</label>
                <div className="bg-gray-50 rounded-lg p-3">
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="text-sm font-medium text-gray-900">
                        {task.risk_action_details.assignedUserObject.first_name}{' '}
                        {task.risk_action_details.assignedUserObject.last_name}
                      </p>
                      <div className="flex items-center text-xs text-gray-500 mt-1">
                        <Mail className="h-3 w-3 mr-1" />
                        {task.risk_action_details.assignedUserObject.email}
                      </div>
                      
                      {task.risk_action_details.assignedUserObject && (
                        <div className="mt-2">
                          <p className="text-xs text-gray-600 mb-1">Rôle:</p>
                          <div className="flex flex-wrap gap-1">
                    
                              <span 
                           
                                className="px-2 py-0.5 bg-blue-100 text-blue-800 text-xs rounded"
                              >
                                {task.risk_action_details.assignedUserObject.role_function}
                              </span>
                           
                          </div>
                        </div>
                      )}
                    </div>

                    {task.risk_action_details.userRoleAssignedObject && (
                      <div className="text-right">
                        <p className="text-xs text-gray-600 mb-1">Rôle assigné:</p>
                        <span className="inline-block px-2 py-1 bg-purple-100 text-purple-800 text-xs font-medium rounded">
                          {task.risk_action_details.userRoleAssignedObject.role_name}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Attachments Section */}
      <div className="bg-white border border-gray-200 rounded-lg p-5 shadow-sm">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center">
            <Paperclip className="h-4 w-4 text-purple-600 mr-2" />
            <h2 className="text-base font-semibold text-gray-900">Pièces jointes</h2>
          </div>
          <div className="flex items-center space-x-3">
            {attachments.length > 0 && (
              <span className="text-xs text-gray-500">
                {attachments.length} fichier{attachments.length > 1 ? 's' : ''}
              </span>
            )}
            {/* Upload button for in_progress and completed tasks */}
            {task && (task.status === 'in_progress' || task.status === 'completed') && (
              <button
                onClick={openUploadModal}
                className="flex items-center space-x-1 px-3 py-1 text-sm text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded transition-colors"
                title="Ajouter des pièces jointes"
              >
                <Upload className="h-4 w-4" />
                <span>Ajouter</span>
              </button>
            )}
          </div>
        </div>
        
        {loadingAttachments ? (
          <div className="flex justify-center py-4">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-teal-600"></div>
          </div>
        ) : attachments.length > 0 ? (
          <div className="space-y-3">
            {attachments.map((attachment) => (
              <div key={attachment.id} className="flex items-center justify-between bg-gray-50 p-3 rounded-lg">
                <div className="flex items-center space-x-3 flex-1 min-w-0">
                  <Paperclip className="h-4 w-4 text-gray-400 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">
                      {attachment.file_name}
                    </p>
                    <div className="flex items-center space-x-4 text-xs text-gray-500 mt-1">
                      <span>{taskService.formatFileSize(attachment.file_size)}</span>
                      <span>Uploadé par {attachment.uploaded_by_name}</span>
                      <span>{formatDateTime(attachment.created_at)}</span>
                    </div>
                    {attachment.description && (
                      <p className="text-xs text-gray-600 mt-1">{attachment.description}</p>
                    )}
                  </div>
                </div>
                <div className="flex items-center space-x-2 ml-3">
                  <button
                    onClick={() => handleDownloadAttachment(attachment.id, attachment.file_name)}
                    className="text-blue-600 hover:text-blue-800 p-1 hover:bg-blue-50 rounded transition-colors"
                    title="Télécharger"
                  >
                    <Download className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => openDeleteModal(attachment.id, attachment.file_name)}
                    className="text-red-600 hover:text-red-800 p-1 hover:bg-red-50 rounded transition-colors"
                    title="Supprimer"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-6">
            <Paperclip className="h-8 w-8 text-gray-300 mx-auto mb-2" />
            <p className="text-sm text-gray-500">Aucune pièce jointe pour cette tâche</p>
          </div>
        )}
      </div>

      {/* Created By & Last Updated By */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        {task.creator_details && (
          <div className="bg-white border border-gray-200 rounded-lg p-5 shadow-sm">
            <div className="flex items-center mb-3">
              <User className="h-4 w-4 text-gray-600 mr-2" />
              <h3 className="text-sm font-semibold text-gray-900">Créé par</h3>
            </div>
            <p className="text-sm text-gray-900">
              {task.creator_details.first_name} {task.creator_details.last_name}
            </p>
            <div className="flex items-center text-xs text-gray-500 mt-1">
              <Mail className="h-3 w-3 mr-1" />
              {task.creator_details.email}
            </div>
          </div>
        )}

        {task.last_updater_details && (
          <div className="bg-white border border-gray-200 rounded-lg p-5 shadow-sm">
            <div className="flex items-center mb-3">
              <Edit className="h-4 w-4 text-gray-600 mr-2" />
              <h3 className="text-sm font-semibold text-gray-900">Dernière modification par</h3>
            </div>
            <p className="text-sm text-gray-900">
              {task.last_updater_details.first_name} {task.last_updater_details.last_name}
            </p>
            <div className="flex items-center text-xs text-gray-500 mt-1">
              <Mail className="h-3 w-3 mr-1" />
              {task.last_updater_details.email}
            </div>
          </div>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      {deleteModal.isOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4 shadow-xl">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Confirmer la suppression</h3>
            <p className="text-gray-600 mb-6">
              Êtes-vous sûr de vouloir supprimer le fichier{' '}
              <span className="font-medium text-gray-900">"{deleteModal.fileName}"</span> ?
            </p>
            <p className="text-sm text-red-600 mb-6">
              Cette action est irréversible.
            </p>
            
            <div className="flex justify-end space-x-3">
              <button 
                onClick={closeDeleteModal} 
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
              >
                Annuler
              </button>
              <button 
                onClick={confirmDelete} 
                className="px-4 py-2 text-white bg-red-600 rounded-lg hover:bg-red-700 transition-colors"
              >
                Supprimer
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Upload Modal */}
      {uploadModal.isOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4 shadow-xl">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              Ajouter des pièces jointes
            </h3>
            <p className="text-gray-600 mb-4">
              Tâche: <span className="font-medium">{task?.name}</span>
            </p>
            
            {/* File upload area */}
            <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 mb-4">
              <input
                type="file"
                multiple
                accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png,.gif"
                onChange={handleFileSelection}
                className="hidden"
                id="upload-file-input"
              />
              <label
                htmlFor="upload-file-input"
                className="cursor-pointer flex flex-col items-center justify-center text-gray-500 hover:text-gray-700"
              >
                <Upload className="h-12 w-12 mb-3" />
                <span className="text-sm font-medium">Cliquez pour sélectionner des fichiers</span>
                <span className="text-xs text-gray-400 mt-1 text-center">
                  PDF, DOC, DOCX, XLS, XLSX, JPG, JPEG, PNG, GIF<br />
                  Taille maximale: 5MB par fichier
                </span>
              </label>
            </div>
            
            {/* Selected files list */}
            {selectedFiles.length > 0 && (
              <div className="mb-4 max-h-40 overflow-y-auto">
                <p className="text-sm font-medium text-gray-700 mb-2">
                  Fichiers sélectionnés ({selectedFiles.length}):
                </p>
                <div className="space-y-2">
                  {selectedFiles.map((file, index) => (
                    <div key={index} className="flex items-center justify-between bg-gray-50 p-2 rounded text-sm">
                      <div className="flex items-center space-x-2 flex-1 min-w-0">
                        <Paperclip className="h-4 w-4 text-gray-400 flex-shrink-0" />
                        <span className="text-gray-700 truncate">{file.name}</span>
                        <span className="text-gray-500 flex-shrink-0">
                          ({taskService.formatFileSize(file.size)})
                        </span>
                      </div>
                      <button
                        onClick={() => removeSelectedFile(index)}
                        className="text-red-500 hover:text-red-700 ml-2"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            <div className="flex justify-end space-x-3">
              <button 
                onClick={closeUploadModal} 
                disabled={isUploading}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors disabled:opacity-50"
              >
                Annuler
              </button>
              <button 
                onClick={handleUploadFiles} 
                disabled={selectedFiles.length === 0 || isUploading}
                className="px-4 py-2 text-white bg-teal-600 rounded-lg hover:bg-teal-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
              >
                {isUploading && <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>}
                <span>{isUploading ? 'Upload...' : 'Uploader'}</span>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}